/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-get, ember/no-runloop, ember/require-tagless-components */
// Extends from
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
// Ember imports
import { bind } from '@ember/runloop';
import { service } from '@ember/service';
// External Imports
import Zoomist from 'zoomist';
import 'zoomist/css';
import MediaSource from '@linc-technologies/linced-common/enums/media-source';
import { isiOS } from '@linc-technologies/linced-common/utils/platform';
import { action } from '@ember/object';

export default class FullscreenAttachment extends Component {
  class = 'fullscreen-attachment';

  @service
  media;

  mediaArray = [];
  @tracked
  interactable = false;

  // Props
  disableZoom = false;

  nextImage() {}
  prevImage() {}

  // Attributes
  @tracked
  isFullscreen = false;

  keyDownHandler = null;

  init() {
    super.init(...arguments);
    this.keyDownHandler = bind(this, this.handleKeyDown);
  }

  setupZoomist() {
    if (!this.isFullscreen) {
      return (this.zoomist = null);
    }
    if (!this.zoomist) {
      const zoomist = new Zoomist('.zoomist-container', {
        maxScale: 5,
        bounds: true,
        zoomer: !this.disableZoom,
        on: {
          zoom: (zoomist, scale) => {
            this.handleZoom(zoomist, scale);
          },
        },
        draggable: true,
        wheelable: true,
        pinchable: true,
      });
      this.zoomist = zoomist;
    }
  }

  handleKeyDown(event) {
    switch (event.code) {
      case 'ArrowLeft': // Left arrow
        this.prevImage();
        break;
      case 'ArrowRight': // Right arrow
        this.nextImage();
        break;
      case 'Escape': // ESC
        this.toggleFullscreen();
        break;
    }
  }

  handleZoom(zoomist, scale) {
    // having the image interactable on carousels removes the ability to use hammer
    // swipe to navigate
    if (scale === 1 && this.mediaArray.length === 1) {
      return (this.interactable = true);
    }
    if (this.interactable === true) {
      this.interactable = false;
    }
  }

  toggleScrolling(disable) {
    let body = document.body;
    if (disable) {
      body.classList.add('no-scroll');
    } else {
      body.classList.remove('no-scroll');
    }
  }

  @action
  toggleFullscreen() {
    if (!this.isFullscreen) {
      document.addEventListener('keydown', this.keyDownHandler);
      this.isFullscreen = true;
    } else {
      document.removeEventListener('keydown', this.keyDownHandler);
      this.isFullscreen = false;
    }
    this.toggleScrolling(this.isFullscreen);
    if (this.mediaArray.length === 1) {
      this.interactable = true;
    }
    // Zoomist is slightly faster than the template renderer, so it will attempt
    // to attach itself to an element that doesnt exist (unless we have a very small timeout)
    setTimeout(() => {
      this.setupZoomist();
    }, 50);
  }

  @action
  async downloadFile() {
    if (this.get('activeItem.mediaSource') === MediaSource.GOOGLE_DRIVE) {
      // Images embedded from Google Drive are difficult; CORS restrictions
      // prevent all methods of fetching the file to blobify - or even the
      // Location header (redirect target) - and also disables the `download`
      // attribute on links (even when redirected).  We therefore need to
      // build a direct link to the Drive `export=download` URL and click it.
      const id = this.get('activeItem.storageKey');
      const key = this.get('activeItem.resourceKey');
      let anchor = document.createElement('a');
      anchor.href = `https://drive.google.com/uc?id=${id}&export=download&resourcekey=${key}`;
      anchor.click();
      anchor.remove();
      return;
    }

    const fileName = this.get('activeItem.fileName');
    try {
      let blobUri = await this.media.getImageBlobUri(
        this.get('activeItem.id'),
        '',
        '',
        'image/png',
      );
      let anchor = document.createElement('a');
      anchor.download = fileName;
      anchor.href = blobUri;
      anchor.click();
      anchor.remove();

      if (!isiOS()) {
        // In iOS, file download and blob revocation is handled by natively
        // injected javascript.
        //
        // We had a race condition where the blob was being nuked before the
        // app could retrieve the file over the native bridge.
        // Refer: https://linced.atlassian.net/browse/HERO-11791?focusedCommentId=42214
        window.URL.revokeObjectURL(blobUri);
      }
    } catch (error) {
      console.error('Download failed', error);
    }
  }

  @action
  handlePrev() {
    this.prevImage();
    this.zoomist.reset();
  }

  @action
  handleNext() {
    this.nextImage();
    this.zoomist.reset();
  }

  @action
  hammerPrev() {
    let transform = this.zoomist.transform;
    if (!transform || !transform.__scale__ || transform.__scale__ !== 1) {
      return;
    }
    this.handlePrev();
  }

  @action
  hammerNext() {
    let transform = this.zoomist.transform;
    if (!transform || !transform.__scale__ || transform.__scale__ !== 1) {
      return;
    }
    this.handleNext();
  }
}
